import { Paperclip, SendHorizonal, Smile, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useSendMessage } from '../../hooks/useTeamChat';
import {
  isSupportedTeamAttachment,
  MAX_TEAM_ATTACHMENT_BYTES,
  uploadTeamChatAttachment,
  type TeamChatAttachment,
  type TeamChatMessage,
} from '../../lib/teamChatApi';
import { Popover } from '../tasks/linear/Popover';
import { UploadChipList } from '../ui/UploadProgress';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { EmojiPicker } from './EmojiPicker';

const MAX_BODY_CHARS = 8000;

/**
 * Every control in the input row is exactly this tall, so a single-line
 * composer lines the placeholder, the emoji button and Send up on one centre.
 * The textarea reaches it through padding (not a min-height) so it can still
 * grow line by line: 24px line-height + 2×6px on mobile, 20px + 2×8px above md.
 * On touchscreens the buttons grow to 44px (index.css), so the textarea's
 * padding grows with them there (.chat-composer-input).
 */
const CONTROL = 'h-9 w-9 shrink-0 flex items-center justify-center rounded-md';

/**
 * Message composer: Enter sends, Shift+Enter adds a newline. Attachments can
 * be picked, dragged in, or pasted; they stage via the team-chat upload
 * endpoint before the message referencing them is sent.
 */
export function Composer({
  conversationId,
  placeholder,
  replyTo,
  onClearReply,
}: {
  conversationId: string;
  placeholder: string;
  replyTo: TeamChatMessage | null;
  onClearReply: () => void;
}) {
  const { t } = useI18n();
  const sendMessage = useSendMessage(conversationId);
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<TeamChatAttachment[]>([]);
  // One chip per file being attached, named and with its own bar, instead of a
  // single anonymous shimmer that said nothing about which file or how far.
  const { uploads, isUploading, run } = useUploadQueue();
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Grow to fit the text. Counting '\n' (the old `rows` calculation) misses
  // wrapped lines, which on a phone is most of them: a long single-line message
  // stayed one row tall and scrolled inside itself.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  const handleFiles = async (files: FileList | File[]) => {
    setError(null);
    for (const file of Array.from(files)) {
      if (!isSupportedTeamAttachment(file)) {
        setError(t('teamChat.unsupportedType', { name: file.name }));
        continue;
      }
      if (file.size > MAX_TEAM_ATTACHMENT_BYTES) {
        setError(t('teamChat.uploadTooLarge', { name: file.name }));
        continue;
      }
      try {
        const attachment = await run(file, (onProgress) =>
          uploadTeamChatAttachment(file, onProgress),
        );
        setAttachments((prev) => [...prev, attachment]);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const submit = () => {
    const trimmed = body.trim();
    if ((!trimmed && attachments.length === 0) || isUploading || sendMessage.isPending) return;
    setError(null);
    sendMessage.mutate(
      { body: trimmed, attachments, replyToId: replyTo?.id ?? null },
      {
        onSuccess: () => {
          setBody('');
          setAttachments([]);
          onClearReply();
          textareaRef.current?.focus();
        },
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setBody((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + emoji.length;
    });
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length > 0) {
      e.preventDefault();
      void handleFiles(files);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={`border-t border-border px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 ${
        dragOver ? 'bg-accent/60' : ''
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      {replyTo && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-accent/60 px-3 py-1.5 text-xs text-muted-foreground">
          <span className="truncate">
            {t('teamChat.replyingTo', { name: replyTo.author.name })}
            {': '}
            <span className="italic">{replyTo.body.slice(0, 80)}</span>
          </span>
          <button
            type="button"
            onClick={onClearReply}
            aria-label={t('common.cancel')}
            className="shrink-0 rounded p-0.5 hover:bg-accent hover:text-foreground"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {(attachments.length > 0 || isUploading) && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {attachments.map((attachment) => (
            <span
              key={attachment.key}
              className="flex items-center gap-1.5 rounded-md border border-border bg-accent/50 px-2 py-1 text-xs text-foreground"
            >
              <Paperclip size={12} className="text-muted-foreground" />
              <span className="max-w-40 truncate">{attachment.fileName}</span>
              <button
                type="button"
                onClick={() =>
                  setAttachments((prev) => prev.filter((a) => a.key !== attachment.key))
                }
                aria-label={t('common.delete')}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {/* Named chips with real progress, so a slow upload says which file
              it is working on and how far it has got. */}
          <UploadChipList uploads={uploads} />
        </div>
      )}

      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

      <div className="flex items-end gap-1 rounded-lg border border-border bg-background px-1.5 py-1.5 focus-within:border-brand-600/50">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          aria-label={t('teamChat.attachFile')}
          title={t('teamChat.attachFile')}
          className={`${CONTROL} text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50`}
        >
          <Paperclip size={18} />
        </button>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY_CHARS))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          onPaste={onPaste}
          placeholder={placeholder}
          rows={1}
          // `text-base` below md is not cosmetic: iOS Safari zooms into any
          // focused field under 16px, and that zoom is what threw the whole
          // composer off the top of the screen when the keyboard opened.
          className="chat-composer-input max-h-40 min-w-0 flex-1 resize-none overflow-y-auto bg-transparent px-1 py-1.5 text-base leading-6 text-foreground outline-none placeholder:text-muted-foreground md:py-2 md:text-sm md:leading-5"
        />
        <Popover
          width={280}
          align="end"
          trigger={({ toggle, ref }) => (
            <button
              type="button"
              ref={(el) => ref(el)}
              onClick={toggle}
              aria-label={t('teamChat.addEmoji')}
              title={t('teamChat.addEmoji')}
              className={`${CONTROL} text-muted-foreground hover:bg-accent hover:text-foreground`}
            >
              <Smile size={18} />
            </button>
          )}
        >
          {(close) => (
            <EmojiPicker
              onPick={(emoji) => {
                insertEmoji(emoji);
                close();
              }}
            />
          )}
        </Popover>
        <button
          type="button"
          onClick={submit}
          disabled={
            (!body.trim() && attachments.length === 0) || isUploading || sendMessage.isPending
          }
          aria-label={t('teamChat.send')}
          title={t('teamChat.send')}
          className={`${CONTROL} bg-brand-600 text-white transition-colors hover:bg-brand-700 disabled:opacity-40`}
        >
          <SendHorizonal size={18} />
        </button>
      </div>
    </div>
  );
}
